/**
 * Phase 296 — Coach QR Attendance Scanner Screen
 *
 * Designed for outdoor use under bright sun. Pure-white background (even
 * if app is in dark mode), max contrast QR, 30-second auto-rotation with
 * countdown ring, live check-in counter + scrolling ticker, manual
 * fallback button.
 *
 * Wires to:
 *   POST /api/coach/sessions/{id}/qr-token       (token mint, 30s exp)
 *   GET  /api/coach/sessions/{id}/live-check-ins (count + recent ticker)
 *
 * URL: /coach/sessions/:sessionId/qr-scanner
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ClipboardList, Smartphone, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// JWT lives for 30 seconds. We refresh 1s before expiry so the QR never
// shows a stale code mid-scan.
const TOKEN_TTL_SECONDS = 30;
const REFRESH_LEAD_MS = 1000;
const LIVE_FEED_POLL_MS = 5000;

// Pure-white background + huge QR are critical for sun-readability.
// Bigger circles tick down — the security countdown ring.
const RING_SIZE = 80;
const RING_RADIUS = (RING_SIZE - 8) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default function CoachQrScannerScreen() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(TOKEN_TTL_SECONDS);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [liveFeed, setLiveFeed] = useState({ count_present: 0, expected_count: 0, recent: [] });
  const [tokenError, setTokenError] = useState(null);
  const refreshTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);
  const livePollRef = useRef(null);
  const wakeLockRef = useRef(null);

  // ── Token mint + auto-rotate ─────────────────────────────────────────
  // The QR value is a deep-link URL (NOT the raw JWT) so native iOS/Android
  // cameras open the parent /scan page in-browser without any app install.
  const buildScanUrl = useCallback((tok) => {
    if (typeof window === 'undefined' || !tok) return tok;
    return `${window.location.origin}/scan?token=${encodeURIComponent(tok)}`;
  }, []);

  const mintToken = useCallback(async () => {
    try {
      const r = await api.post(`/coach/sessions/${sessionId}/qr-token`);
      setToken(r.data.token);
      setSessionMeta({ title: r.data.session_title, batch: r.data.batch });
      setSecondsLeft(r.data.expires_in_seconds || TOKEN_TTL_SECONDS);
      setTokenError(null);
    } catch (err) {
      setTokenError(err?.response?.data?.detail || 'Failed to mint QR token');
      toast.error('Could not generate QR — refresh the page or contact admin.');
    }
  }, [sessionId]);

  useEffect(() => {
    mintToken();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
    };
  }, [mintToken]);

  // Tick the countdown ring + schedule the next mint right before expiry
  useEffect(() => {
    if (!token) return undefined;
    setSecondsLeft(TOKEN_TTL_SECONDS);
    if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    tickIntervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    refreshTimerRef.current = setTimeout(
      () => { mintToken(); },
      TOKEN_TTL_SECONDS * 1000 - REFRESH_LEAD_MS,
    );
    return () => {
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [token, mintToken]);

  // ── Live feed polling for the counter + ticker ───────────────────────
  const fetchLiveFeed = useCallback(async () => {
    try {
      const r = await api.get(`/coach/sessions/${sessionId}/live-check-ins`);
      setLiveFeed(r.data || { count_present: 0, expected_count: 0, recent: [] });
    } catch {
      // Silent — counter just stays at the last known value
    }
  }, [sessionId]);

  useEffect(() => {
    fetchLiveFeed();
    livePollRef.current = setInterval(fetchLiveFeed, LIVE_FEED_POLL_MS);
    return () => { if (livePollRef.current) clearInterval(livePollRef.current); };
  }, [fetchLiveFeed]);

  // ── Screen Wake Lock — keep the phone screen ON during pickup window ──
  useEffect(() => {
    const acquireWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch {
        // Browser may reject (battery, focus loss) — non-critical.
      }
    };
    acquireWakeLock();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquireWakeLock();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
    };
  }, []);

  const presentCount = liveFeed.count_present || 0;
  const expectedCount = liveFeed.expected_count || 0;
  const recent = liveFeed.recent || [];

  // Progress for the countdown ring (1.0 → full, 0.0 → empty)
  const progress = secondsLeft / TOKEN_TTL_SECONDS;
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress);

  return (
    // Force-white background even in dark mode for outdoor sun-readability.
    <div
      className="min-h-screen bg-white text-slate-900 flex flex-col"
      data-testid="coach-qr-scanner-screen"
      style={{ colorScheme: 'light' }}
    >
      {/* Slim top bar — minimal so the QR dominates the screen */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-700 hover:bg-slate-100"
          onClick={() => navigate(-1)}
          data-testid="coach-qr-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="text-center">
          <p className="text-xs font-semibold text-slate-700 truncate max-w-[200px]">
            {sessionMeta?.title || 'Session check-in'}
          </p>
          {sessionMeta?.batch && (
            <p className="text-[10px] text-slate-500 truncate max-w-[200px]">{sessionMeta.batch}</p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-slate-700"
          onClick={() => navigate('/coach')}
          data-testid="coach-qr-manual-fallback-btn"
        >
          <ClipboardList className="h-3.5 w-3.5 mr-1" /> Manual
        </Button>
      </div>

      {/* QR + countdown ring */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 space-y-4">
        {tokenError ? (
          <div
            className="rounded-lg border border-rose-300 bg-rose-50 p-4 max-w-md text-center"
            data-testid="coach-qr-error-state"
          >
            <AlertTriangle className="h-8 w-8 text-rose-600 mx-auto mb-2" />
            <p className="text-sm font-semibold text-rose-700 mb-1">QR generation failed</p>
            <p className="text-xs text-rose-600 mb-3">{tokenError}</p>
            <Button onClick={mintToken} className="bg-rose-600 hover:bg-rose-700 text-white">
              Try again
            </Button>
          </div>
        ) : token ? (
          <>
            {/* Hi-contrast QR — black on white, large, with a thick white border
                so phone autofocus locks on cleanly even in sun glare. */}
            <div
              className="relative bg-white border-[8px] border-white rounded-2xl shadow-2xl"
              style={{ width: 'min(60vw, 360px)', height: 'min(60vw, 360px)' }}
              data-testid="coach-qr-code-canvas"
            >
              <QRCodeSVG
                value={buildScanUrl(token)}
                size={Math.min(window.innerWidth * 0.6, 360) - 16}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#000000"
                style={{ width: '100%', height: '100%' }}
              />
              {/* Countdown ring overlaid bottom-right corner */}
              <div
                className="absolute -bottom-3 -right-3 bg-white rounded-full p-1 shadow-md"
                data-testid="coach-qr-countdown-ring"
              >
                <div className="relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
                  <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
                    <circle
                      cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS}
                      fill="none" stroke="#e2e8f0" strokeWidth="6"
                    />
                    <circle
                      cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS}
                      fill="none"
                      stroke={secondsLeft > 10 ? '#10b981' : secondsLeft > 3 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="6"
                      strokeDasharray={RING_CIRCUMFERENCE}
                      strokeDashoffset={dashOffset}
                      strokeLinecap="round"
                      transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                      style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }}
                    />
                  </svg>
                  {/* HTML overlay for the countdown number — Tailwind preflight forces
                      display:block on SVG <text> which breaks glyph layout, so we
                      render the number as an absolutely positioned span instead. */}
                  <span
                    className="absolute inset-0 flex items-center justify-center font-display font-bold tabular-nums text-slate-900 pointer-events-none"
                    style={{ fontSize: 22, lineHeight: 1 }}
                    data-testid="coach-qr-countdown-number"
                  >
                    {secondsLeft}
                  </span>
                </div>
              </div>
            </div>

            {/* Instruction — kept minimal, white space speaks louder */}
            <p className="text-xs text-slate-500 text-center max-w-md">
              <Smartphone className="h-3 w-3 inline mr-1" />
              Parents — scan with your phone camera to check your child in.
              Code refreshes every 30 seconds for security.
            </p>
          </>
        ) : (
          <div className="text-slate-400 text-sm" data-testid="coach-qr-loading">
            Generating QR…
          </div>
        )}
      </div>

      {/* Live counter + ticker — the dopamine hit */}
      <div className="bg-slate-50 border-t border-slate-200 px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Live check-ins</p>
          <p
            className="text-3xl font-display font-bold text-emerald-600 tabular-nums"
            data-testid="coach-qr-live-counter"
          >
            {presentCount}
            {expectedCount > 0 && (
              <span className="text-base text-slate-500 font-normal"> / {expectedCount}</span>
            )}
            <span className="text-xs text-slate-500 font-normal ml-2">checked in</span>
          </p>
        </div>
        {/* Scrolling ticker — most recent first */}
        <div
          className="h-12 overflow-hidden border-t border-slate-200 pt-2"
          data-testid="coach-qr-recent-ticker"
        >
          {recent.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              Waiting for the first check-in…
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {recent.slice(0, 3).map((row) => (
                <p
                  key={`${row.student_id}-${row.at}`}
                  className="text-xs text-slate-700 truncate"
                  data-testid={`coach-qr-ticker-row-${row.student_id}`}
                >
                  <span className="text-emerald-600 font-bold">✓</span>{' '}
                  <span className="font-semibold">{row.student_name}</span>{' '}
                  <span className="text-slate-400">just checked in</span>{' '}
                  {row.method === 'qr_scan' && (
                    <span className="text-[10px] text-indigo-500 ml-1">via QR</span>
                  )}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
